import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import { IconArrowDown, IconArrowUp, IconMinus } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";

export interface ChapterPassRateTrendProps {
  /** Pass rate of the selected grading, as a normalized 0-1 fraction. */
  currentPassRate: number | null | undefined;
  /**
   * Pass rate of the grading immediately before the selected one, or
   * undefined when there is no previous grading to compare against.
   */
  previousPassRate: number | null | undefined;
}

export const ChapterPassRateTrend: FC<ChapterPassRateTrendProps> = (props) => {
  if (props.currentPassRate == null || props.previousPassRate == null) {
    return null;
  }

  // Round both values up front so the diff in the tooltip stays consistent
  // with the percentages it cites.
  const currentPercent = Math.round(props.currentPassRate * 100);
  const previousPercent = Math.round(props.previousPassRate * 100);
  const diffPoints = currentPercent - previousPercent;

  const Icon =
    diffPoints > 0 ? IconArrowUp : diffPoints < 0 ? IconArrowDown : IconMinus;
  const colorClass =
    diffPoints === 0
      ? "text-gray-400"
      : diffPoints > 0
        ? "text-success"
        : "text-destructive";

  const signedDiff = diffPoints > 0 ? `+${diffPoints}` : `${diffPoints}`;
  const tooltipText =
    diffPoints === 0
      ? "No change vs. last grade"
      : `${signedDiff} percentage points vs. last grade (${previousPercent}% to ${currentPercent}%)`;

  return (
    <TooltipTrigger>
      <TooltipHoverTarget shape="rounded">
        <Icon className={twMerge("size-4", colorClass)} aria-hidden="true" />
      </TooltipHoverTarget>
      <SimpleTooltip>{tooltipText}</SimpleTooltip>
    </TooltipTrigger>
  );
};
